// React.
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

// Types.
import { InstanceOptions, ParameterComparisonOptionsCardProps } from "./types";

// Constants.
import { ERROR_MESSAGE_DATES } from "./constants";

// Components.
import Alert from "../../../component/Alert";
import ConditionalRender from "../../../helpers/ConditionalRender";
import InstanceComparisonOptions from "../instance-comparison-options";

const ParameterComparisonOptionsCard = (props: ParameterComparisonOptionsCardProps) => {
    const [firstInstanceOptions, setFirstInstanceOptions] = useState<InstanceOptions>(props.options.firstInstanceOptions);
    const [secondInstanceOptions, setSecondInstanceOptions] = useState<InstanceOptions>(props.options.secondInstanceOptions);

    const allRequiredOptionsSet = useCallback(() => {
        return (firstInstanceOptions.instanceId > -1 &&
            firstInstanceOptions.periodEnd &&
            firstInstanceOptions.periodStart &&
            secondInstanceOptions.instanceId > -1 &&
            secondInstanceOptions.periodStart &&
            secondInstanceOptions.periodEnd && validDates)
    }, [props.options, firstInstanceOptions, secondInstanceOptions])

    useEffect(() => {
        if (firstInstanceOptions.instanceId !== -1 && firstInstanceOptions.periodEnd && firstInstanceOptions.periodStart) {
            props.onOptionsChange('firstInstanceOptions', firstInstanceOptions)
        }

        if (secondInstanceOptions.instanceId === -1) {
            setSecondInstanceOptions({
                ...secondInstanceOptions,
                instanceId: firstInstanceOptions.instanceId,
                instanceName: firstInstanceOptions.instanceName,
                instanceType: firstInstanceOptions.instanceType
            })
        }
    }, [firstInstanceOptions, props.options.firstInstanceOptions])

    useEffect(() => {
        if (secondInstanceOptions.instanceId !== -1 && secondInstanceOptions.periodEnd && secondInstanceOptions.periodStart) {
            props.onOptionsChange('secondInstanceOptions', secondInstanceOptions)
        }
    }, [secondInstanceOptions])

    const validDates = useMemo(() => {
        return firstInstanceOptions.periodStart < firstInstanceOptions.periodEnd && secondInstanceOptions.periodStart < secondInstanceOptions.periodEnd
    }, [firstInstanceOptions, secondInstanceOptions])

    const resetFilters = () => {
        props.resetFilters()

        setSecondInstanceOptions({
            instanceId: -1,
            instanceType: undefined,
            periodStart: new Date(dayjs().subtract(1, 'hour').toString()),
            periodEnd: new Date(),
            instanceName: '',
            snapshotId: NaN,
        })
        setFirstInstanceOptions({
            instanceId: -1,
            instanceType: undefined,
            periodStart: new Date(dayjs().subtract(2, 'hour').toString()),
            periodEnd: new Date(dayjs().subtract(1, 'hour').toString()),
            instanceName: '',
            snapshotId: NaN,
        })
    }


    return <div className="card collapsible">
        <div className="card-header">
            <i className="fal fa-stopwatch fa-fw" aria-hidden="true"/>
            Parameter comparison options
            <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseSettings"
               aria-expanded="false" aria-controls="collapseSettings"/>
        </div>
        <div id="collapseSettings" className="card-body collapse show">
            <ConditionalRender if={!validDates}>
                <Alert message={ERROR_MESSAGE_DATES.INVALID.TEXT} heading={ERROR_MESSAGE_DATES.INVALID.TITLE}
                       variant="alert-danger"/>
            </ConditionalRender>
            <div className='row row-cols-lg-12'>
                <InstanceComparisonOptions instanceKey={'firstInstanceOptions'}
                                           loading={props.loading} {...firstInstanceOptions}
                                           instances={props.instances}
                                           onOptionsChange={setFirstInstanceOptions}/>
                <InstanceComparisonOptions instanceKey={'secondInstanceOptions'}
                                           loading={props.loading} {...secondInstanceOptions}
                                           instances={props.instances}
                                           onOptionsChange={setSecondInstanceOptions}/>

            </div>

            <div className='row row-cols-lg-12 mt-3'>
                <div className="col-xs-2 col-md-9 col-lg-9"/>
                <div className="col-xs-2 col-md-3 col-lg-3 d-flex align-items-end justify-end">
                    <button className="btn btn-dark mx-2 text-nowrap"
                            disabled={props.loading}
                            onClick={() => resetFilters()}>
                        <i className="far fa-undo fa-fw fa-sm"/><span>Reset</span>
                    </button>
                    <button className="btn btn-success text-nowrap"
                            disabled={!props.loading && !allRequiredOptionsSet()}
                            onClick={() => props.onGenerate()}>
                        <i className="fal fa-play fa-fw fa-sm"/><span>Generate</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
}

export default ParameterComparisonOptionsCard;
